type Appearance = "sdx--light-theme" | "sdx--dark-theme";

/**
 * Returns the parent element of a given element.
 * Also checks shadow roots and slots.
 * @param el Element to read its parent from.
 */
export function parent(el: Element): Element | undefined {
  if (el.assignedSlot) {
    return el.assignedSlot;
  }

  if (el.parentElement) {
    return el.parentElement;
  }

  const rootNode = el.getRootNode() as ShadowRoot;

  if (rootNode.host) {
    return rootNode.host;
  }

  return undefined;
}

/**
 * Traverses up from a given element and compares each parent until first match.
 * @param el Element to start from.
 * @param selector A selector (can be a string, an element or a function).
 */
export function closest(
  el: Element | null,
  selector: string | Element | ((el: Element) => boolean),
): Element | undefined {
  let currentEl: Element | undefined | null = el;

  while (currentEl) {
    if (
      (typeof selector === "string" && currentEl.matches(selector)) ||
      (typeof selector === "object" && currentEl === selector) ||
      (typeof selector === "function" && selector(currentEl))
    ) {
      return currentEl;
    }

    currentEl = parent(currentEl);
  }

  return undefined;
}

export function isTextNode(node: Node): node is Element {
  return node.nodeType === Node.TEXT_NODE;
}

export function isElementNode(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE;
}

/**
 * Checks if a given node has a child with a given slot name (if any).
 * @param el Parent element to check.
 * @param name Slot name to check.
 */
export function hasSlot(el: Element, name?: string): boolean {
  if (name) {
    // This also works when el has no shadow root (e.g. sdx-header-menu)
    return !!el.querySelector(`[slot=${name}]`);
  } else {
    // Warning: this only works when el has a shadow root
    return [...el.childNodes].some(
      node =>
        (isTextNode(node) && node.textContent && /\S/.test(node.textContent)) ||
        (isElementNode(node) && !node.hasAttribute("slot")),
    );
  }
}

export function isTouchscreen(): boolean {
  return "ontouchstart" in window;
}

export function getAppearance(el: HTMLElement): Appearance {
  const elWithDarkTheme = closest(el, ".sdx--dark-theme");

  if (elWithDarkTheme) {
    return "sdx--dark-theme";
  }

  return "sdx--light-theme";
}

export function getIconLinkClassNames(href?: string, target?: string) {
  let linkClass = "link--standalone";

  if (target === "_blank") {
    linkClass = "link--external";
  }

  if (href?.startsWith("#")) {
    linkClass = "link--anchor";
  }

  return `link ${linkClass}`;
}
